// Pure helpers behind the dark product stages of the Fundamentals, Pulse and Random
// Coffee previews. They follow the product's own rules (cooperly_app
// features/pulse/pulse-stage.ts and features/fundamentals/fundamentals-stage.ts) so
// the demo reads the same numbers the same way.

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

type CalendarDate = { year: number; month: number; day: number };

/** Reads `YYYY-MM-DD` without a timezone, so a demo date never shifts by a day. */
function parseCalendarDate(value: string): CalendarDate {
  const [year, month, day] = value.split("-").map(Number);

  return { year, month: month - 1, day };
}

function addDays(date: CalendarDate, days: number): CalendarDate {
  const shifted = new Date(Date.UTC(date.year, date.month, date.day + days));

  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth(),
    day: shifted.getUTCDate(),
  };
}

/** `Apr 3`, as the product writes dates on the stage. */
export function formatShortDate(value: string) {
  const date = parseCalendarDate(value);

  return `${MONTHS_SHORT[date.month]} ${date.day}`;
}

/** `April 3`. */
export function formatLongDate(value: string) {
  const date = parseCalendarDate(value);

  return `${MONTHS_LONG[date.month]} ${date.day}`;
}

/** `May 18–24`, or `Apr 27–May 3` when the week crosses a month. */
export function formatWeekRange(weekStart: string) {
  const start = parseCalendarDate(weekStart);
  const end = addDays(start, 6);
  const startLabel = `${MONTHS_LONG[start.month]} ${start.day}`;

  if (end.month === start.month) {
    return `${startLabel}–${end.day}`;
  }

  return `${MONTHS_SHORT[start.month]} ${start.day}–${MONTHS_SHORT[end.month]} ${end.day}`;
}

/** `79%`, rounded like the product's formatPercent. */
export function formatPercent(value: number) {
  return `${Math.round(value)}%`;
}

/** A 1–5 score with one decimal, like the product's formatScore. */
export function formatScore(value: number) {
  return value.toFixed(1);
}

export type PulseStageCycle = {
  /** The cycle's first day, `YYYY-MM-DD`. */
  startsOn: string;
  ok: number;
  notOk: number;
  /** People asked in this cycle. */
  expected: number;
  isOpen: boolean;
};

/**
 * A bar as shares of the people who answered: OK from the bottom, Not OK above it,
 * so the split is the OK share whatever the team size.
 */
export function pulseShareParts(cycle: PulseStageCycle) {
  const answered = cycle.ok + cycle.notOk;

  return {
    ok: answered > 0 ? cycle.ok / answered : 0,
    notOk: answered > 0 ? cycle.notOk / answered : 0,
    answered,
    expected: cycle.expected,
  };
}

export function pulseOkPercent(cycle: PulseStageCycle) {
  const answered = cycle.ok + cycle.notOk;

  return answered > 0 ? (cycle.ok / answered) * 100 : 0;
}

/** The stage figures for a run of cycles: average, extremes, completion and the latest cycle. */
export function pulseSummary(cycles: readonly PulseStageCycle[]) {
  const answeredCycles = cycles.filter((cycle) => cycle.ok + cycle.notOk > 0);
  const percents = answeredCycles.map(pulseOkPercent);
  const ok = answeredCycles.reduce((sum, cycle) => sum + cycle.ok, 0);
  const answered = cycles.reduce((sum, cycle) => sum + cycle.ok + cycle.notOk, 0);
  const expected = cycles.reduce((sum, cycle) => sum + cycle.expected, 0);
  const latest = cycles[cycles.length - 1];

  return {
    averagePercent: answered > 0 ? (ok / answered) * 100 : 0,
    minPercent: percents.length > 0 ? Math.min(...percents) : 0,
    maxPercent: percents.length > 0 ? Math.max(...percents) : 0,
    answered,
    expected,
    latest,
  };
}

export type ScoreBand = "low" | "mid" | "high";

/** Band of a 1–5 score as displayed (one decimal): below 3, 3 to 4, 4 and above. */
export function scoreBand(average: number): ScoreBand {
  const displayed = Math.round(average * 10) / 10;

  if (displayed < 3) {
    return "low";
  }

  return displayed < 4 ? "mid" : "high";
}

/** Share of the 1–5 track, clamped so a stray value never leaves the plot. */
export function scalePosition(value: number) {
  return (Math.min(Math.max(value, 1), 5) - 1) / 4;
}
